// Thin client for the Cloudflare Worker control plane. The browser transacts;
// these calls record, resolve, and read. Never in the tx-submission path.

// 1.4.34 — the Worker's answers come from shared/api.ts, the one copy the Worker checks
// its responses against too: a field changed on one side fails tsc on the other.
import type { AccessRequested, DemoConfig, MerchantCheck, InviteConsumed, InviteGate, InviteValidation, NameResolved, PendingTxList, Profile, TimelockHold, TimelockPosture, TimelockProfile, TimelockView, XpayBalances, XpayQuote, XpayRecorded } from "../../shared/api";
export type { DemoConfig, InviteGate, InviteValidation, PendingTx, Profile } from "../../shared/api";

const BASE = import.meta.env.VITE_WORKER_URL ?? "http://localhost:8787";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Worker ${path} → ${res.status} ${body}`);
  }
  return (await res.json()) as T;
}

export interface WalletName {
  slug: string;
  display: string;
  url: string;
}

export interface WalletRecord {
  id: number;
  address: string;
  explorerUrl: string;
}

export interface WalletView {
  address: string;
  usdcBalance: string | null;
  status: string;
  name: { slug: string; display: string } | null;
}

export interface TxRecord {
  id: number;
  status: string;
  explorerUrl: string;
}

export interface FundingResult {
  id: number;
  status: string; // requested | submitted | confirmed | failed
  amount: string;
}

export const recordWallet = (address: string, appUserLabel: string) =>
  req<WalletRecord>("/wallet/record", {
    method: "POST",
    body: JSON.stringify({ address, appUserLabel }),
  });

export const getWallet = (address: string) =>
  req<WalletView>(`/wallet/${address}`);

export const recordTx = (body: {
  address: string;
  txHash?: string;
  userOpHash?: string;   // v0.90.26 — recorded at acceptance; txHash follows
  success?: boolean;     // v0.90.26 — with txHash: reverted userOps are recorded as failed
  currency?: string;     // "USD" | "EUR"
  kind?: "send" | "drop_create" | "drop_rest" | "drop_release" | "earn_in" | "earn_out" | "pay";
  dropId?: string;
  direction: "send" | "receive";
  amount: string;
  counterparty: string;
  ref?: string;          // v0.99.0 — the reference as shown (≤ 40 chars); Omega Air matches its order on it
}) => {
  return req<TxRecord>("/tx/record", {
    method: "POST",
    body: JSON.stringify(body),
  });
};

// v0.90.26 — payments recorded at acceptance and not yet (or never) confirmed.
export const getPendingTx = (address: string) =>
  req<PendingTxList>(`/tx/pending/${address}`);

export const getTx = (id: number | string) =>
  req<{ status: string }>(`/tx/${id}`);

// v0.19 "Add money": one-tap request → poll until confirmed. $10, one-off.
export const requestFunding = (address: string, idempotencyKey: string) =>
  req<FundingResult>("/funding/request", {
    method: "POST",
    body: JSON.stringify({ address, idempotencyKey }),
  });

export const getFunding = (id: number | string) =>
  req<{ status: string; amount: string; txHash: string | null }>(
    `/funding/${id}`,
  );

/** Delete this wallet's control-plane records (name + history). */
export const deleteWallet = (address: string) =>
  req<{ ok: boolean }>("/wallet/delete", {
    method: "POST",
    body: JSON.stringify({ address }),
  });

// --- wallet names (v0.11) ---------------------------------------------------

/** A fresh candidate name (no DB write) — used as the passkey label at register. */
export const proposeName = () =>
  req<{ slug: string; display: string }>("/name/propose");

/**
 * Assign (or fetch existing) this wallet's name — idempotent per address.
 * Pass desiredSlug (the label the passkey was registered with) to keep the OS
 * picker label and the stored name in sync.
 */
const assignNameNow = (address: string, desiredSlug?: string) =>
  req<WalletName>("/name/assign", {
    method: "POST",
    body: JSON.stringify({ address, desiredSlug }),
  });

/** 1.4.75 — started as soon as a wallet exists (createView, right after Face ID), so the home's own
 *  assignName finds it already in flight: the name and the invite then cost one round trip together. */
const earlyName = new Map<string, Promise<WalletName>>();
export function prefetchName(address: string, desiredSlug?: string): void {
  const k = `${address.toLowerCase()}|${desiredSlug ?? ""}`;
  if (earlyName.has(k)) return;
  const p = assignNameNow(address, desiredSlug);
  p.catch(() => earlyName.delete(k)); // a failed early start is retried by the next call
  earlyName.set(k, p);
}
export const assignName = (address: string, desiredSlug?: string): Promise<WalletName> => {
  const k = `${address.toLowerCase()}|${desiredSlug ?? ""}`;
  const p = earlyName.get(k);
  if (p) { earlyName.delete(k); return p.catch(() => assignNameNow(address, desiredSlug)); } // an early start that failed is tried once more
  return assignNameNow(address, desiredSlug);
};

/** The wallet's existing name, or nulls if none yet. */
export const getNameOf = (address: string) =>
  req<{ slug: string | null; display: string | null; url: string | null }>(
    `/name/of/${address}`,
  );

/** Resolve a pay-by-name slug → recipient { slug, display, address }. */
export const resolveName = (slug: string) =>
  req<NameResolved>(`/name/resolve/${slug}`);

// --- invites (v0.20) --------------------------------------------------------



export interface InviteCode {
  code: string; // display form, e.g. "4KT-9RM"
  status: string; // active | used | revoked
  shared?: boolean; // issuer completed a native share
  joinedName?: string | null; // display (code) of the wallet that used this code
  joinedAddr?: string | null; // that wallet's address — keys its device nickname
}

export interface MyInvites {
  codes: InviteCode[];
  joined: number;
  needed: number;
  unlocked: boolean;
}

/** Is the create-gate on? App reads this at the start of the create path. */
export const getInviteGate = () => req<InviteGate>("/invite/gate");

/** Pre-create check — never consumes. Returns 200 with { valid } either way. */
/** 1.4.41 — Request access (Worker 1.4.4): an email for the list of invites. Never throws. */
export async function requestAccess(email: string): Promise<"ok" | "invalid" | "busy" | "error"> {
  try {
    const res = await fetch(`${BASE}/access/request`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (res.status === 400) return "invalid";
    if (res.status === 429) return "busy";
    if (!res.ok) return "error";
    return ((await res.json()) as AccessRequested).ok ? "ok" : "error";
  } catch {
    return "error";
  }
}

export const validateInvite = (code: string) =>
  req<InviteValidation>(`/invite/validate/${encodeURIComponent(code)}`);

/**
 * Atomically claim a code for this wallet at create time. Non-throwing: a lost
 * race / already-used / network error comes back as { ok:false } so the caller
 * can decide (the passkey wallet already exists on-chain regardless).
 */
export const consumeInvite = async (
  code: string,
  address: string,
): Promise<{ ok: boolean; code?: string; error?: string; welcome?: string }> => {
  try {
    // v1.3.1 — Worker 1.3.2: `welcome` is the dollars about to be sent to the
    // new wallet ("10", "1"); absent when nothing will be sent (or an older Worker).
    const r = await req<InviteConsumed>("/invite/consume", {
      method: "POST",
      body: JSON.stringify({ code, address }),
    });
    return { ok: true, code: r.code, welcome: typeof r.welcome === "string" ? r.welcome : undefined };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
};

/** Issue this wallet's initial codes (idempotent) — called on create. */
export const mintInvites = (address: string) =>
  req<{ codes: InviteCode[] }>("/invite/mint", {
    method: "POST",
    body: JSON.stringify({ address }),
  });

/** This wallet's codes + conversion progress, for the "Your invites" sheet. */
export const getMyInvites = (address: string) =>
  req<MyInvites>(`/invite/mine/${address}`);

/** Flag a code as shared (only on a completed native share). Fire-and-forget. */
export const markInviteShared = (code: string, address: string) =>
  req<{ ok: boolean }>("/invite/mark-shared", {
    method: "POST",
    body: JSON.stringify({ code, address }),
  });

// --- v0.70 Drops ------------------------------------------------------------
// "A payment to someone not on M is an invite with money attached." The Worker
// records and reads; the escrow (MDrop on Arc) is the truth and the sender's
// passkey is the only key that moves the money.

export interface DropConfig {
  configured: boolean;
  contract: string | null; // legacy (USD)
  usdc: string;            // legacy (USD)
  /** v0.80.31 — one escrow per currency: { USD: {contract, token}, EUR: {...} } */
  contracts?: Record<string, { contract: string; token: string }>;
  /** v0.90.21 — MDropBearer per currency: the first payment to someone new. */
  bearer?: Record<string, { contract: string; token: string }>;
  ttlDays: number;
}
export interface DropReservation {
  id: string;
  idBytes32: `0x${string}`;
  amountUnits: string;
}
export type DropStatus = "open" | "claimed" | "released" | "cancelled" | "refunded";
export interface DropView {
  id: string;
  currency?: string; // v0.80.31 — "USD" | "EUR"
  amount: string; // "25.00", in `currency`
  ref: string | null;
  label: string;
  senderCode: string | null; // XXXX-XXXX (link slug — plumbing, never typed)
  senderName: string | null;
  senderEmoji: string | null;
  claimerCode: string | null;
  claimerName: string | null;
  claimerEmoji: string | null;
  status: DropStatus;
  expiresAt: string | null;
  createdAt: string;
  releasedAt?: string | null;
  txCreate?: string | null;
  txRelease?: string | null;
  // only on /drop/mine (your own contacts): lets the app overlay nicknames
  senderAddress?: string;
  claimerAddress?: string | null;
  /** v0.90.21 — a bearer Drop (MDropBearer): paid on claim, no release. */
  bearer?: boolean;
  /** v0.90.21 — the amount the sender is holding back until "got it". */
  rest?: string | null;
  restStatus?: "held" | "sent" | "dropped" | null;
}

export const getProfile = (address: string) => req<Profile>(`/profile/${address}`);
export const setProfile = (body: {
  address: string;
  name?: string | null;
  emoji?: string | null;
  settings?: string; // v0.80.40 — the wallet's own preferences (client-owned JSON)
}) =>
  req<Profile>("/profile", { method: "POST", body: JSON.stringify(body) });

let dropConfigCache: Promise<DropConfig> | null = null;
export const getDropConfig = () =>
  (dropConfigCache ??= req<DropConfig>("/drop/config").catch((e) => {
    dropConfigCache = null;
    throw e;
  }));

export const reserveDrop = (body: {
  senderAddress: string;
  label: string;
  amount: string;
  ref?: string;
  currency?: string; // v0.80.31 — which escrow; USD if omitted
  bearer?: boolean;  // v0.90.21 — MDropBearer
  claimKey?: string; // v0.90.21 — the claim key's ADDRESS (never the key)
  rest?: string;     // v0.90.21 — held back until the recipient says "got it"
}) => req<DropReservation>("/drop/reserve", { method: "POST", body: JSON.stringify(body) });

export const dropCreated = (id: string, txHash: string) =>
  req<DropView>(`/drop/${id}/created`, { method: "POST", body: JSON.stringify({ txHash }) });

export const getDrop = (id: string) => req<DropView>(`/drop/${id}`);

export const claimDrop = (id: string, claimerAddress: string) =>
  req<DropView>(`/drop/${id}/claim`, { method: "POST", body: JSON.stringify({ claimerAddress }) });

/** v0.90.21 — a bearer Drop was claimed on chain; the Worker reads who was paid. */
export const dropClaimed = (id: string, txHash: string) =>
  req<DropView>(`/drop/${id}/claimed`, { method: "POST", body: JSON.stringify({ txHash }) });

/** v0.90.21 — the held rest was sent (txHash) or dropped. */
export const dropRest = (id: string, body: { senderAddress: string; txHash?: string; dropped?: boolean }) =>
  req<DropView>(`/drop/${id}/rest`, { method: "POST", body: JSON.stringify(body) });

export const dropReleased = (id: string, txHash: string) =>
  req<DropView>(`/drop/${id}/released`, { method: "POST", body: JSON.stringify({ txHash }) });

export const dropCancelled = (id: string, txHash: string) =>
  req<DropView>(`/drop/${id}/cancelled`, { method: "POST", body: JSON.stringify({ txHash }) });

export const getMyDrops = (address: string) =>
  req<{ sent: DropView[]; received: DropView[] }>(`/drop/mine/${address}`);

/** The link the sender shares: opaque id, nothing else in the URL. */
// v0.90.21 — a bearer Drop's link carries its claim key after `#`: browsers
// never send a fragment, so neither mdrop.it nor a link-preview bot sees it.
export const dropUrl = (origin: string, id: string, claimKey?: string) =>
  `${origin}/d/${id}${claimKey ? `#${claimKey}` : ""}`;
/** The link the claimer sends back. */
export const dropConfirmUrl = (origin: string, id: string) => `${origin}/c/${id}`;

// --- v0.70.12 Activity (statement via Arcscan, decorated by the Worker) -----
export type ActivityKind = "send" | "receive" | "drop_out" | "drop_in" | "drop_back" | "fund" | "earn_in" | "earn_out" | "swap_out" | "swap_in" | "note_out" | "note_in";
export interface ActivityItem {
  /** v0.80.28 — which pocket this row belongs to ("USD" | "EUR"). */
  currency?: string;
  hash: string;
  at: string;
  amount: string;
  sign: "+" | "-";
  kind: ActivityKind;
  counterpart: string;
  code: string | null;
  name: string | null;
  emoji: string | null;
  dropId?: string;
  label?: string | null;
  ref?: string | null;
  /** v0.90.13 — both legs of a swap against MFx, so a row can read `Swap · $5.00 → €4.45`. */
  swap?: { from: string; fromAmount: string; to: string; toAmount: string };
  /** v1.1.15 — Worker 1.1.0: the note's serial on a note_out / note_in leg, when the app reported it. */
  note?: string;
}
export interface ActivityTiming {
  explorer_ms: number;
  pages: number;
  explorer?: string; // v0.90.22 — "ok" | "unavailable: …"
  own?: { legs: number; fetched: number; rpc_ms: number; error?: string }; // v0.90.22 — M's own legs from the node
}
export const getActivity = (
  address: string,
  month: string,
  limit?: number,
  currency?: string,
  fresh?: boolean, // v0.80.29 — skip the Worker's 60 s cache (after a payment)
) =>
  req<{ month: string; items: ActivityItem[]; timing?: ActivityTiming; worker?: string }>(
    `/activity/${address}?month=${encodeURIComponent(month)}${limit ? `&limit=${limit}` : ""}${
      currency ? `&currency=${encodeURIComponent(currency)}` : ""
    }${fresh ? "&fresh=1" : ""}`,
    // 1.4.47 — the browser asks the Worker every time: the answer carries max-age=60, and a browser
    // that kept an answer from before a payment served it again for a minute after a fresh read
    // (the Worker's own edge cache, which a fresh read refreshes, still answers quickly).
    { cache: "no-cache" },
  );

// v0.99.0 — "Pay with M®": the Omega Air checkout demo. The More row shows only
// while the Worker says the demo is on; the token opens the site for this wallet.
export const getDemoConfig = () => req<DemoConfig>("/demo/config");
export const demoToken = (address: string) =>
  req<{ token: string; url: string; expiresIn: number }>("/demo/token", {
    method: "POST",
    body: JSON.stringify({ address }),
  });

/** 1.5.17 — M® Checkout on a computer: has the phone paid this reference to this payee? (Worker 1.4.16, GET /pay/status) */
/** 1.5.21 (Worker 1.4.17) — is this registered merchant site paid into this wallet? `site` null: not a registered site. */
export async function merchantCheck(site: string, to: string): Promise<MerchantCheck> {
  const q = new URLSearchParams({ site, to });
  const r = await fetch(`${BASE}/merchant/check?${q}`);
  if (!r.ok) throw new Error(`merchant/check ${r.status}`);
  return (await r.json()) as MerchantCheck;
}

/** 1.5.21 — the one sentence for a payee that isn't the site's wallet, on the checkout and on the phone. */
export function mismatchLine(siteName: string): string {
  return `This isn't ${siteName}'s wallet.`;
}

export async function payStatus(to: string, ref: string, amount: string, currency: string): Promise<{ status: "none" | "paid" | "settled" | "failed" | "cancelled"; txHash?: string | null; explorerUrl?: string | null }> {
  const q = new URLSearchParams({ to, ref, amount, currency });
  const r = await fetch(`${BASE}/pay/status?${q}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`pay/status ${r.status}`);
  return (await r.json()) as { status: "none" | "paid" | "settled" | "failed" | "cancelled"; txHash?: string | null; explorerUrl?: string | null };
}

// --- 1.5.29 — outside wallets at M® Checkout (Worker 1.4.21) --------------------------------------------------------
export type { XpayBalances, XpayQuote, XpayRecorded } from "../../shared/api";
/** A wallet's USDC on every chain M reads (shared/xchains.ts). */
export const xpayBalances = (address: string) => req<XpayBalances>(`/xpay/balances?address=${encodeURIComponent(address)}`, { cache: "no-store" });
/** What a payment from `chain` burns so the merchant receives `amount` on Arc (Circle's fee, read just now). */
export const xpayQuote = (chain: string, amount: string) => req<XpayQuote>(`/xpay/quote?chain=${encodeURIComponent(chain)}&amount=${encodeURIComponent(amount)}`, { cache: "no-store" });
/** The payment, read and recorded by the Worker for the order. A refusal throws with the Worker's reason
 *  (wrong_payee, short, another_order, tx_used …) in the message. */
export const xpayRecord = (b: { to: string; ref: string | null; amount: string; currency: string; chain: string; txHash: string }) =>
  req<XpayRecorded>("/xpay/record", { method: "POST", body: JSON.stringify(b) });

// --- the Timelock's stand-in (app 1.5.59, Worker 1.4.23; app 1.5.60, Worker 1.4.24; claude/timelock.md §8, §12) ----------
export type { TimelockHold, TimelockPosture, TimelockProfile, TimelockView } from "../../shared/api";
const tlPost = <T>(path: string, body: unknown) => req<T>(path, { method: "POST", body: JSON.stringify(body) });
export const getTimelock = (address: string) => req<TimelockView>(`/timelock/${address}`);
/** Only what changes (Worker 1.4.24); the first set-up names a posture. */
export const setTimelockPolicy = (b: { address: string; posture?: TimelockPosture; profile?: TimelockProfile; salt?: string; wordC?: string; maydayC?: string; overUsd?: number; holdH?: number }) => tlPost<TimelockView>("/timelock/policy", b);
export const cancelTimelockChange = (address: string) => tlPost<{ ok: true }>("/timelock/policy/cancel", { address });
export const holdPayment = (b: { address: string; token: string; amount: string; to: string; ref?: string; trigger: string }) => tlPost<{ hold: TimelockHold }>("/timelock/hold", b);
export const releaseHold = (b: { address: string; id: string; k: string }) => tlPost<{ ok: true; goesAt: number }>("/timelock/release", b);
export const cancelHold = (b: { address: string; id: string }) => tlPost<{ ok: true }>("/timelock/cancel", b);
/** `txHash`: the userOp's hash at acceptance (the Worker takes any 32-byte hash) — so a tab that dies before the receipt
 *  doesn't offer the same payment again. */
export const holdSent = (b: { address: string; id: string; txHash: string }) => tlPost<{ ok: true }>("/timelock/sent", b);
